import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { spanForSize } from "../grid.utils";

interface AddWidgetTileProps {
  columns: number;
  /** Widgets in the catalogue that are not on the canvas yet. */
  remaining: number;
  onClick: () => void;
}

/**
 * The "add widget" slot, in the grid flow rather than only in the command bar so
 * the affordance sits where the widget will actually land. Carries the count of
 * what is left, which is the answer to the question the button raises.
 */
export const AddWidgetTile = ({
  columns,
  remaining,
  onClick,
}: AddWidgetTileProps) => {
  const { t } = useTranslation(["common"]);
  const span = spanForSize("sm", columns);
  const isExhausted = remaining === 0;

  return (
    <div
      role="listitem"
      style={{ gridColumn: `span ${span.w}`, gridRow: "span 1" }}
      className="min-h-0"
    >
      <button
        type="button"
        // Fallback focus target once the last widget is removed.
        data-add-widget
        onClick={onClick}
        disabled={isExhausted}
        className="widget-slot widget-focus group flex size-full flex-col items-center justify-center gap-1 text-muted-foreground transition-colors hover:border-primary hover:bg-primary-muted/60 hover:text-primary disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:border-primary/40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
      >
        <span className="grid size-7 place-items-center rounded-full border border-border bg-surface transition-transform group-enabled:group-hover:scale-110">
          <Plus size={14} aria-hidden />
        </span>
        <span className="text-widget-label font-semibold">
          {isExhausted
            ? t("common:dashboard_grid.all_widgets_added")
            : t("common:dashboard_grid.add_widget")}
        </span>
        {!isExhausted && (
          <span className="text-widget-caption text-muted-foreground">
            {t("common:dashboard_grid.count_available", { count: remaining })}
          </span>
        )}
      </button>
    </div>
  );
};
